// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 3 — PBL 마을학교(아젠다 ①). 공개 모집·운영중 프로그램 목록 + 운영자 생성/전이.

const STATUS_FLOW: Record<string, string[]> = {
  draft:        ["recruiting", "archived"],
  recruiting:   ["in_progress", "archived"],
  in_progress:  ["completed", "archived"],
  completed:    ["archived"],
  archived:     [],
};

export function ProgramsPanel({ bureaus, isOperator, user }: { bureaus: V.Bureau[]; isOperator: boolean; user: any | null }) {
  const toast = useToast();
  const [status, setStatus] = React.useState("");
  const [list, setList] = React.useState<V.Program[] | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [detail, setDetail] = React.useState<V.Program | null>(null);

  const reload = React.useCallback(() => {
    V.programs.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [status, toast]);
  React.useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>PBL 마을학교</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 140 }}>
            <option value="">전체 상태</option>
            {Object.entries(V.LABELS.programStatus).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          {isOperator && <Button onClick={() => setCreating(true)}>프로그램 생성</Button>}
        </div>
      </div>

      {list === null ? <Loading /> :
       list.length === 0 ? <Empty title="해당 조건의 프로그램이 없습니다." /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((p) => (
            <Card key={p.id} onClick={() => setDetail(p)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {p.title}
                    <Badge tone={toneForProgramStatus(p.status)}>{V.LABELS.programStatus[p.status]}</Badge>
                    <Badge tone="neutral">{p.bureau.name}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 4, lineHeight: 1.6 }}>
                    {p.summary || p.topic || "요약 없음"}
                  </div>
                  <div style={{ fontSize: 11, color: TOKENS.ink3, marginTop: 4 }}>
                    정원 {p.capacity ?? "-"} · 학년 {gradeRange(p.min_grade, p.max_grade)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      }

      <ProgramEditor
        open={creating}
        bureaus={bureaus}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); reload(); }}
      />

      <ProgramDetail
        open={!!detail}
        program={detail}
        isOperator={isOperator}
        user={user}
        onClose={() => setDetail(null)}
        onChanged={() => { setDetail(null); reload(); }}
      />
    </div>
  );
}

function ProgramEditor({ open, bureaus, onClose, onSaved }: {
  open: boolean; bureaus: V.Bureau[]; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = React.useState("");
  const [bureauCode, setBureauCode] = React.useState<V.BureauCode>("L");
  const [summary, setSummary] = React.useState("");
  const [topic, setTopic] = React.useState("");
  const [capacity, setCapacity] = React.useState("");
  const [minGrade, setMinGrade] = React.useState("");
  const [maxGrade, setMaxGrade] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(""); setBureauCode("L"); setSummary(""); setTopic("");
    setCapacity(""); setMinGrade(""); setMaxGrade("");
    setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.programs.create({
        bureau_code: bureauCode,
        title: title.trim(),
        summary: summary || undefined,
        topic: topic || undefined,
        capacity: capacity ? Number(capacity) : undefined,
        min_grade: minGrade ? Number(minGrade) : undefined,
        max_grade: maxGrade ? Number(maxGrade) : undefined,
      });
      toast.push("프로그램을 초안으로 생성했습니다.", "success");
      onSaved();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const activeBureaus = bureaus.filter((b) => b.kind === "bureau");

  return (
    <Modal
      open={open}
      title="PBL 프로그램 생성"
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting || !title.trim()}>
            {submitting ? "생성 중…" : "초안으로 생성"}
          </Button>
        </>
      }
    >
      <Field label="프로그램 제목" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="담당 교육국">
          <Select value={bureauCode} onChange={(e) => setBureauCode(e.target.value as V.BureauCode)}>
            {activeBureaus.map((b) => (
              <option key={b.code} value={b.code}>{b.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="주제 태그">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="예: 생태·환경" />
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label="정원">
          <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        </Field>
        <Field label="최소 학년">
          <Input type="number" min={1} max={12} value={minGrade} onChange={(e) => setMinGrade(e.target.value)} />
        </Field>
        <Field label="최대 학년">
          <Input type="number" min={1} max={12} value={maxGrade} onChange={(e) => setMaxGrade(e.target.value)} />
        </Field>
      </div>
      <Field label="요약">
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} />
      </Field>
    </Modal>
  );
}

function ProgramDetail({ open, program, isOperator, user, onClose, onChanged }: {
  open: boolean; program: V.Program | null; isOperator: boolean; user: any | null;
  onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [participants, setParticipants] = React.useState<V.Participant[] | null>(null);
  const [applying, setApplying] = React.useState(false);
  const [transitioning, setTransitioning] = React.useState(false);

  const reload = React.useCallback(() => {
    if (!program) return;
    V.participants.list(program.id)
      .then((r) => setParticipants(r.items))
      .catch(() => setParticipants([]));
  }, [program]);

  React.useEffect(() => { if (open) reload(); }, [open, reload]);

  if (!program) return null;

  const transition = async (to: string) => {
    setTransitioning(true);
    try {
      await V.programs.transition(program.id, to);
      toast.push(`${V.LABELS.programStatus[to]} 상태로 전이했습니다.`, "success");
      onChanged();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setTransitioning(false);
    }
  };

  const nextStates = STATUS_FLOW[program.status] || [];
  const canApply = !isOperator && program.status === "recruiting" && !!user;

  return (
    <Modal
      open={open}
      title={program.title}
      onClose={onClose}
      width={640}
      footer={
        <>
          {isOperator && nextStates.length > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              {nextStates.map((to) => (
                <Button key={to} variant="secondary" size="sm" disabled={transitioning}
                  onClick={() => transition(to)}>
                  → {V.LABELS.programStatus[to]}
                </Button>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {canApply && <Button onClick={() => setApplying(true)}>참여 신청</Button>}
          <Button variant="ghost" onClick={onClose}>닫기</Button>
        </>
      }
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge tone={toneForProgramStatus(program.status)}>{V.LABELS.programStatus[program.status]}</Badge>
        <Badge tone="neutral">{program.bureau.name}</Badge>
        {program.topic && <Badge tone="info">{program.topic}</Badge>}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.7, color: TOKENS.ink2 }}>
        {program.summary || "요약이 등록되지 않았습니다."}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: TOKENS.ink2, marginBottom: 12 }}>
        <div>정원: {program.capacity ?? "-"}</div>
        <div>학년: {gradeRange(program.min_grade, program.max_grade)}</div>
      </div>

      <h3 style={{ fontSize: 13, fontWeight: 700, margin: "12px 0 8px" }}>참여자</h3>
      {participants === null ? <Loading /> :
       participants.length === 0 ? <Empty title="아직 참여 신청이 없습니다." /> :
        <div style={{ display: "grid", gap: 6 }}>
          {participants.map((p) => (
            <ParticipantRow
              key={p.id} participant={p} program={program}
              isOperator={isOperator} user={user}
              onChanged={reload}
            />
          ))}
        </div>
      }

      <ApplyParticipantModal
        open={applying}
        program={program}
        user={user}
        onClose={() => setApplying(false)}
        onApplied={() => { setApplying(false); reload(); }}
      />
    </Modal>
  );
}

function ParticipantRow({ participant: p, program, isOperator, user, onChanged }: {
  participant: V.Participant; program: V.Program; isOperator: boolean; user: any | null; onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  const act = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    try { await fn(); toast.push(msg, "success"); onChanged(); }
    catch (e: any) { toast.push(errorMessage(e), "danger"); }
    finally { setBusy(false); }
  };
  const canCancel = !!user && (user.id === p.user?.id || isOperator);

  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 12px", border: `1px solid ${TOKENS.borderSoft}`, borderRadius: 8, background: TOKENS.surface,
    }}>
      <div style={{ fontSize: 13 }}>
        <strong>{p.youth?.name || p.user?.name || "미지정"}</strong>
        {p.youth && <span style={{ color: TOKENS.ink3 }}> · {p.youth.grade ?? "-"}학년</span>}
        &nbsp;<Badge tone="neutral">{V.LABELS.participantRole[p.role]}</Badge>
        &nbsp;<Badge tone={p.status === "accepted" || p.status === "attended" ? "success" : p.status === "declined" || p.status === "cancelled" ? "danger" : "warning"}>
          {V.LABELS.participantStatus[p.status]}
        </Badge>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {isOperator && p.status === "applied" && (
          <>
            <Button size="sm" disabled={busy}
              onClick={() => act(() => V.participants.accept(program.id, p.id), "수락했습니다.")}>수락</Button>
            <Button variant="secondary" size="sm" disabled={busy}
              onClick={() => act(() => V.participants.decline(program.id, p.id), "거절했습니다.")}>거절</Button>
          </>
        )}
        {isOperator && p.status === "accepted" && (
          <Button variant="secondary" size="sm" disabled={busy}
            onClick={() => act(() => V.participants.attended(program.id, p.id), "출석 처리했습니다.")}>출석</Button>
        )}
        {canCancel && (p.status === "applied" || p.status === "accepted") && (
          <Button variant="ghost" size="sm" disabled={busy}
            onClick={() => act(() => V.participants.cancel(program.id, p.id), "취소했습니다.")}>취소</Button>
        )}
      </div>
    </div>
  );
}

function ApplyParticipantModal({ open, program, user, onClose, onApplied }: {
  open: boolean; program: V.Program; user: any | null; onClose: () => void; onApplied: () => void;
}) {
  const toast = useToast();
  const [role, setRole] = React.useState("participant");
  const [youthId, setYouthId] = React.useState("");
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setRole("participant"); setYouthId(""); setNote(""); setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload: any = { role, note: note || undefined };
      if (role === "participant") {
        if (!youthId) throw { details: ["참여자(청소년) ID 를 입력해 주세요."] };
        payload.youth_id = Number(youthId);
      }
      await V.participants.apply(program.id, payload);
      toast.push("참여 신청을 접수했습니다.", "success");
      onApplied();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`${program.title} 참여 신청`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</Button>
        </>
      }
    >
      <Field label="역할">
        <Select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="participant">참여자 (청소년)</option>
          <option value="teacher">교사</option>
          <option value="mentor">멘토</option>
          <option value="parent">학부모</option>
        </Select>
      </Field>
      {role === "participant" && (
        <Field label="참여자 Youth ID" required
               hint="주 보호자로 연결된 청소년의 ID를 입력하세요. (현장 운영 전환 전 임시)">
          <Input value={youthId} onChange={(e) => setYouthId(e.target.value)} />
        </Field>
      )}
      <Field label="메모">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

function toneForProgramStatus(s: string): any {
  if (s === "recruiting") return "primary";
  if (s === "in_progress") return "info";
  if (s === "completed") return "success";
  if (s === "archived") return "neutral";
  return "warning"; // draft
}
function gradeRange(min: number | null, max: number | null) {
  if (min == null && max == null) return "제한 없음";
  if (min == null) return `~${max}학년`;
  if (max == null) return `${min}학년~`;
  return `${min}~${max}학년`;
}
